import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Card, CardContent, Popover } from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import CloudIcon from '@mui/icons-material/Cloud';
import GrainIcon from '@mui/icons-material/Grain';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ThunderstormIcon from '@mui/icons-material/Bolt';
import WbCloudyIcon from '@mui/icons-material/WbCloudy';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import { useSelector, useDispatch } from 'react-redux';
import { fetchWeatherData } from '../features/weather/weatherSlice';

// 根據香港天文台天氣代碼返回適當的圖標
const getWeatherIcon = (iconCode, condition) => {
  const code = String(iconCode || '');
  const conditionText = String(condition || '').toLowerCase();
  
  // 參考香港天文台圖標代碼：https://www.hko.gov.hk/tc/wxinfo/dailywx/dhkoicon.shtml
  if (['50', '51', '52', '53', '90', '91'].includes(code) || 
      conditionText.includes('晴') || conditionText.includes('sunny') || conditionText.includes('clear')) {
    return <WbSunnyIcon style={{ color: '#FFD700' }} />;
  } else if (['54', '92'].includes(code) || conditionText.includes('陰') || conditionText.includes('cloudy')) {
    return <WbCloudyIcon style={{ color: '#A9A9A9' }} />;
  } else if (code >= '60' && code <= '68' || conditionText.includes('雨') || conditionText.includes('rain')) {
    return <BeachAccessIcon style={{ color: '#1E90FF' }} />;
  } else if (['70', '71', '72', '73', '74', '75'].includes(code) || 
             conditionText.includes('雷') || conditionText.includes('thunder')) {
    return <ThunderstormIcon style={{ color: '#9370DB' }} />;
  } else if (['76', '77'].includes(code) || conditionText.includes('雪') || conditionText.includes('snow')) {
    return <AcUnitIcon style={{ color: '#FFFFFF' }} />;
  } else if (['80', '81', '82', '83', '84', '85'].includes(code)) {
    return <NightsStayIcon style={{ color: '#555555' }} />;
  } else if (['55', '56', '57', '58', '59', '93', '94', '95'].includes(code) || 
             conditionText.includes('霧') || conditionText.includes('fog')) {
    return <GrainIcon style={{ color: '#D3D3D3' }} />;
  } else {
    return <CloudIcon style={{ color: '#D3D3D3' }} />;
  }
};

const WeatherWidget = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useDispatch();
  const { currentWeather, forecast, loading, error } = useSelector((state) => state.weather);

  useEffect(() => {
    // 加載天氣數據
    dispatch(fetchWeatherData());
    
    // 每30分鐘刷新一次天氣
    const intervalId = setInterval(() => {
      dispatch(fetchWeatherData());
    }, 30 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [dispatch]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // 如果正在加載或出錯，顯示適當的指示
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
        <Typography variant="body2" color="inherit">加載中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
        <Typography variant="body2" color="inherit">無法加載天氣</Typography>
      </Box>
    );
  }

  if (!currentWeather) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
        <Typography variant="body2" color="inherit">無天氣數據</Typography>
      </Box>
    );
  }

  // 顯示當前天氣
  return (
    <>
      <Tooltip title="點擊查看天氣預報">
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mr: 2,
            cursor: 'pointer'
          }}
          onClick={handleClick}
        >
          {getWeatherIcon(currentWeather.icon, currentWeather.condition)}
          <Typography variant="body2" color="inherit" sx={{ ml: 1 }}>
            {currentWeather.temperature}°C
          </Typography>
        </Box>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Card sx={{ maxWidth: 350, minWidth: 300 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {currentWeather.location || '本地天氣'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ mr: 2 }}>
                {getWeatherIcon(currentWeather.icon, currentWeather.condition)}
              </Box>
              <Box>
                <Typography variant="h4">
                  {currentWeather.temperature}°C
                </Typography>
                <Typography variant="body2">
                  {currentWeather.condition}
                </Typography>
                <Typography variant="caption">
                  濕度: {currentWeather.humidity}% | 風速: {currentWeather.windSpeed} km/h
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem', mt: 0.5 }}>
                  更新時間: {new Date(currentWeather.updateTime).toLocaleTimeString()}
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="subtitle1">未來天氣預報</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', mt: 1 }}>
              {forecast && forecast.map((day, index) => (
                <Box key={index} sx={{ minWidth: 70, textAlign: 'center', mr: 1 }}>
                  <Typography variant="caption">{day.date}</Typography>
                  <Box sx={{ my: 1 }}>
                    {getWeatherIcon(day.icon, day.condition)}
                  </Box>
                  <Typography variant="body2">
                    {day.high}°/{day.low}°
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                    {day.condition.length > 6 
                      ? day.condition.substring(0, 6) + '...' 
                      : day.condition}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Popover>
    </>
  );
};

export default WeatherWidget; 